/* eslint-disable no-nested-ternary */
import { Select, SelectProps } from "antd";
import cn from "classnames";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";

import { useLoc, useUpdateEffect } from "../lib/hooks";

type DateSplitPickerProps = Pick<SelectProps, "size" | "disabled"> & {
  value?: moment.Moment | null;
  defaultValue?: moment.Moment;
  className?: string;
  // TODO: add settings to hide or show parts of picker
  onChange?: (value: moment.Moment) => void;
};

export const DateSplitPicker: React.FC<DateSplitPickerProps> = ({
  value,
  defaultValue,
  className,
  onChange,
  ...props
}) => {
  const loc = useLoc();
  const [day, setDay] = useState(value?.date() ?? defaultValue?.date());
  const [month, setMonth] = useState(
    value
      ? // eslint-disable-next-line no-unsafe-optional-chaining
        value?.month() + 1
      : defaultValue
        ? defaultValue.month() + 1
        : undefined,
  );
  const [year, setYear] = useState(value?.year() ?? defaultValue?.year());
  const daysInMonth = useMemo(() => {
    if (month === undefined || year === undefined) return 31;

    return moment({
      day: 1,
      month: month - 1,
      year,
    }).daysInMonth();
  }, [month, year]);

  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  );

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const years = useMemo(() => {
    const currentYear = moment().year();

    return Array.from({ length: 100 }, (_, i) => currentYear - i);
  }, []);

  const date = useMemo(() => {
    if (day === undefined || month === undefined || year === undefined)
      return undefined;

    return moment({
      day,
      month: month - 1,
      year,
    });
  }, [day, month, year]);

  const update = useCallback(
    (dateObj: { day?: number; month?: number; year?: number }) => {
      let internalDay = dateObj.day ?? day ?? 1;
      const internalMonth = dateObj.month ?? month ?? 1;
      const internalYear = dateObj.year ?? year ?? moment().year();
      // check if month or year changed and update day if needed
      if (dateObj.month !== undefined || dateObj.year !== undefined) {
        const internalDaysInMonth = moment({
          day: 1,
          month: internalMonth - 1,
          year: internalYear,
        }).daysInMonth();
        internalDay = Math.min(internalDaysInMonth, internalDay);
      }

      setDay(internalDay);
      setMonth(internalMonth);
      setYear(internalYear);

      if (
        internalDay === undefined ||
        internalMonth === undefined ||
        internalYear === undefined
      )
        return;

      onChange?.(
        moment({
          day: internalDay,
          month: internalMonth - 1,
          year: internalYear,
        }),
      );
    },
    [day, month, onChange, year],
  );

  const reset = useCallback(() => {
    setDay(undefined);
    setMonth(undefined);
    setYear(undefined);
  }, []);

  // trigger date change when value prop changes
  useUpdateEffect(() => {
    if (!value) {
      reset();

      return;
    }

    if (
      !value.isSame(date, "day") ||
      !value.isSame(date, "month") ||
      !value.isSame(date, "year")
    ) {
      update({
        day: value?.date(),
        // eslint-disable-next-line no-unsafe-optional-chaining
        month: value?.month() + 1,
        year: value?.year(),
      });
    }
  }, [value]);

  return (
    <div className={cn(className, "flex flex-1 gap-5")}>
      <Select
        {...props}
        placeholder={loc.word("DateSplitPicker.dayPlaceholder")}
        value={day}
        options={days.map((dayOption) => ({
          value: dayOption,
          label: dayOption,
        }))}
        onChange={(changedDay) => update({ day: changedDay })}
        style={{ flex: 1 }}
      />

      <Select
        {...props}
        placeholder={loc.word("DateSplitPicker.monthPlaceholder")}
        value={month}
        options={months.map((monthOption) => ({
          value: monthOption,
          label: loc.word(`Month.${monthOption}`),
        }))}
        onChange={(nextMonth) => update({ month: nextMonth })}
        style={{ flex: 2 }}
      />

      <Select
        {...props}
        placeholder={loc.word("DateSplitPicker.yearPlaceholder")}
        value={year}
        options={years.map((yearOption) => ({
          value: yearOption,
          label: yearOption,
        }))}
        onChange={(nextYear) => update({ year: nextYear })}
        style={{ flex: 1 }}
      />
    </div>
  );
};
